import React from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Pagination,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { APP_COLORS as C } from "../../theme/colors";

const AMBER = "#ed6c02";

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
}

function initials(name) {
  return (name || "?")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

/**
 * Admin-only review queue: every submitted sketch from every user, oldest
 * first, with who sent it. Approve / Reject are shown but can be disabled
 * (reviewEnabled=false) until the review workflow is switched on.
 */
export default function AwaitingApproval({
  items,
  loading,
  activeId,
  page,
  pages,
  total,
  pageSize,
  reviewEnabled,
  onView,
  onApprove,
  onReject,
  onPage,
  onRefresh,
}) {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const soon = reviewEnabled ? "" : "Approval workflow — coming soon";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Box sx={{ px: 2, pt: 1.5, pb: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, color: C.primary, lineHeight: 1.2 }}>
            Awaiting approval
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {total} request{total === 1 ? "" : "s"} · oldest first
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={onRefresh}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 1.5, pb: 1, position: "relative" }}>
        {loading && (
          <Box sx={{ position: "absolute", top: 4, right: 16 }}>
            <CircularProgress size={18} />
          </Box>
        )}

        {!loading && items.length === 0 && (
          <Box sx={{ textAlign: "center", color: C.textMuted, px: 2, py: 5 }}>
            <TaskAltIcon sx={{ fontSize: 40, opacity: 0.5 }} />
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
              Nothing is waiting for approval
            </Typography>
            <Typography variant="caption">
              Sketches submitted by users appear here.
            </Typography>
          </Box>
        )}

        {items.map((s) => {
          const active = s.sketch_id === activeId;
          return (
            <Box
              key={s.sketch_id}
              sx={{
                mb: 1,
                p: 1.25,
                pl: 1.75,
                borderRadius: 2,
                bgcolor: active ? C.primaryLight : C.surface,
                border: `1px solid ${active ? C.primaryMid : C.border}`,
                borderLeft: `4px solid ${AMBER}`,
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: C.text }} noWrap>
                {s.name}
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1, my: 0.75 }}>
                <Avatar sx={{ width: 24, height: 24, fontSize: 11, bgcolor: C.primaryMid }}>
                  {initials(s.created_by_name)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.1 }}>
                    Requested by
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
                    {s.created_by_name || `User #${s.created_by}`}
                    {s.created_by_username && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75, fontWeight: 400 }}>
                        @{s.created_by_username}
                      </Typography>
                    )}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 1.5, color: C.textMuted }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <PlaceOutlinedIcon sx={{ fontSize: 14 }} />
                  <Typography variant="caption">
                    {s.feature_count} feature{s.feature_count === 1 ? "" : "s"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <ScheduleIcon sx={{ fontSize: 14 }} />
                  <Typography variant="caption">
                    Submitted {formatDate(s.submitted_at || s.updated_at)}
                  </Typography>
                </Box>
              </Box>

              {s.description && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }} display="block" noWrap>
                  {s.description}
                </Typography>
              )}

              <Box sx={{ display: "flex", gap: 0.75, mt: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<VisibilityOutlinedIcon />}
                  onClick={() => onView(s)}
                >
                  View
                </Button>
                <Tooltip title={soon}>
                  <span>
                    <Button
                      size="small"
                      color="success"
                      variant="contained"
                      disableElevation
                      startIcon={<CheckCircleIcon />}
                      disabled={!reviewEnabled}
                      onClick={() => onApprove(s)}
                    >
                      Approve
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={soon}>
                  <span>
                    <Button
                      size="small"
                      color="error"
                      variant="contained"
                      disableElevation
                      startIcon={<CancelIcon />}
                      disabled={!reviewEnabled}
                      onClick={() => onReject(s)}
                    >
                      Reject
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          );
        })}
      </Box>

      {pages > 0 && (
        <Box
          sx={{
            borderTop: `1px solid ${C.border}`,
            px: 1.5,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "#fafbfc",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {from}–{to} of {total}
          </Typography>
          <Pagination size="small" count={pages} page={page} siblingCount={0} boundaryCount={1} color="primary" onChange={(_, p) => onPage(p)} />
        </Box>
      )}
    </Box>
  );
}
